import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router';
import { RecordScopeProvider, type RecordScope } from '@/api/RecordContext';
import { MeContext } from './MeContext';
import type { Me } from '@/api/session';

export interface CompanyOrigin {
  to: string;
  label: string;
  scopeDepartment: string;
}
const COMPANY =
  /^\/companies\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\?[^#\r\n]*)?$/i;
const RECORD =
  /^\/(tasks|projects|prospects|suppliers|buyers|signals|requests|mail|offers|contacts)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const KIND: Record<string, string> = {
  tasks: 'task',
  projects: 'project',
  prospects: 'prospect',
  suppliers: 'supplier',
  buyers: 'buyer',
  signals: 'signal',
  requests: 'request',
  mail: 'thread',
  offers: 'offer',
};

export function companyOrigin(value: unknown): CompanyOrigin | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (
    typeof v.to !== 'string' ||
    !COMPANY.test(v.to) ||
    typeof v.scopeDepartment !== 'string' ||
    !/^[a-z][a-z_]*$/.test(v.scopeDepartment)
  )
    return null;
  return { to: v.to, label: 'Company', scopeDepartment: v.scopeDepartment };
}
export function isCompanyRecordPath(pathname: string): boolean {
  return RECORD.test(pathname);
}
export function scopeAt(pathname: string, origin: CompanyOrigin): RecordScope | null {
  const record = RECORD.exec(pathname);
  if (!record || record[1]!.toLowerCase() === 'contacts') return null;
  return {
    companyId: origin.to.split('?')[0]!.split('/')[2]!,
    sourceKind: KIND[record[1]!.toLowerCase()]!,
    sourceId: record[2]!,
    scopeDepartment: origin.scopeDepartment,
  };
}

/** History state carries origin through nested records and reload. It carries
 * identifiers only; every read/action is rechecked by companyRecordCall. */
export function CompanyRouteBoundary({ me, children }: { me: Me; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const action = useNavigationType();
  const previous = useRef<CompanyOrigin | null>(null);
  const state = (location.state ?? {}) as Record<string, unknown>;
  const explicit = companyOrigin(state.companyReturn);
  const inherited =
    !Object.hasOwn(state, 'companyReturn') && action !== 'POP' && RECORD.test(location.pathname)
      ? previous.current
      : null;
  const candidate = explicit ?? inherited;
  const origin =
    candidate && (me.isSuperadmin || me.departments.includes(candidate.scopeDepartment))
      ? candidate
      : null;
  const scope = origin ? scopeAt(location.pathname, origin) : null;
  useEffect(() => {
    if (inherited && origin)
      void navigate(location.pathname + location.search, {
        replace: true,
        preventScrollReset: true,
        state: { ...state, companyReturn: inherited },
      });
    if (origin && RECORD.test(location.pathname)) previous.current = origin;
    else previous.current = null;
  }, [
    location.key,
    origin?.to,
    origin?.scopeDepartment,
    scope?.sourceId,
    me.departments,
    me.isSuperadmin,
  ]);
  if (!scope) return <>{children}</>;
  return (
    <RecordScopeProvider scope={scope}>
      <MeContext.Provider value={{ ...me, department: scope.scopeDepartment }}>
        {children}
      </MeContext.Provider>
    </RecordScopeProvider>
  );
}
